import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { proItemDesignApi } from '../api/pro-item-design.adapter';
import { useProItemDesignPageStore } from '../store/pro-item-design-page.store';
import { mergeDraftIntoTree } from '../utils/draft-node';

export const proItemDesignQueryKeys = {
  all: ['pro-item-design'] as const,
  tree: () => [...proItemDesignQueryKeys.all, 'tree'] as const,
  item: (id: number) => [...proItemDesignQueryKeys.all, 'item', id] as const,
};

// PRD §3.1 — full tree, unpaginated. Returns ProDomain[] (with ProItemHeader
// rows under each sub-area). Item details are loaded separately via
// `useProItemQuery` once a leaf is selected.
export function useProTreeQuery() {
  return useQuery({
    queryKey: proItemDesignQueryKeys.tree(),
    queryFn: () => proItemDesignApi.getTree(),
  });
}

// SHCT-388 — the server tree with the in-progress Add draft merged in. Both the
// tree panel and the detail router consume this so an unsaved node shows and is
// selectable before it is persisted. The merge is memoised so the draft node
// keeps a stable object identity across renders (ItemDetail's reset effect keys
// on it).
export function useMergedProTree() {
  const treeQuery = useProTreeQuery();
  const draft = useProItemDesignPageStore((s) => s.draft);
  const serverTree = treeQuery.data;
  const tree = useMemo(
    () => mergeDraftIntoTree(serverTree ?? [], draft),
    [serverTree, draft],
  );
  return { tree, isLoading: treeQuery.isLoading, isError: treeQuery.isError };
}

// PRD §3.2 — full item detail for the right-panel form. Distinct cache key
// per item id; createItem seeds this cache so a freshly added item opens
// without a spinner (see use-pro-mutations.ts `useCreateItem`).
export function useProItemQuery(itemId: number | null) {
  return useQuery({
    queryKey: itemId !== null ? proItemDesignQueryKeys.item(itemId) : ['pro-item-design', 'item', 'noop'],
    queryFn: () => proItemDesignApi.getItem(itemId as number),
    enabled: itemId !== null,
  });
}
